<template>
  <section class="datasets-upload-page">
    <header class="datasets-upload-page__header">
      <section class="datasets-upload-page__header-title">
        <h1 v-if="dataset">Replace Dataset Request</h1>
        <h1 v-else>New Dataset Request</h1>
        <p class="datasets-upload-page--error" v-if="formHasError">
          Please complete all required fields.
        </p>
      </section>
    </header>
    <form @submit.prevent="submitForm()" class="datasets-upload-page__form">
      <section class="datasets-upload-page__form-section">
        <div
          class="datasets-upload-page__form-container datasets-upload-page__form-container--meta-data"
        >
          <label
            for="upload-company-id"
            class="datasets-upload-page__form-label"
          >
            <span>Company Name</span>
            <IconAlertCircle v-if="!user.meta.company_name" />
          </label>
          <input
            id="upload-company-id"
            :value="user.meta.company_name"
            type="text"
            disabled
            class="datasets-upload-page__display-field"
            :class="{
              'datasets-upload-page--is-invalid': !user.meta.company_name,
            }"
          />
        </div>
        <div
          class="datasets-upload-page__form-container datasets-upload-page__form-container--meta-data"
          v-if="showPortalData && !isLoading && !dataset"
        >
          <label
            for="upload-is-consulting"
            class="datasets-upload-page__form-label"
            :class="form.error.isPortalData ? 'extra-gap' : ''"
          >
            <span>Will this dataset be used for your consulting report?</span>
            <span class="datasets-upload-page__icon-container">
              <BaseTooltip italic content-width="large" placement="top">
                <template #display>
                  <IconInformationCircle color="light-grey" />
                  <IconAlertCircle v-if="form.error.isPortalData" color="red" />
                </template>
                <template #tooltip>
                  <p>
                    <span class="bold">Select "No"</span>
                    for this question if the data upload is only to be used in
                    your DataCast or Alliance program.
                  </p>
                  <p>
                    <span class="bold">Select "Yes"</span>
                    for this question if:
                  </p>
                  <ul class="no-margin">
                    <li>
                      The dataset will appear in an ITR Economics generated
                      report either as a dataset to be forecast or as a
                      comparison chart.
                    </li>
                    <li>
                      OR the dataset is meant to support the analysis of a
                      dataset that will appear in an ITR Economics generated
                      report (for example, if we forecast Sales you may choose
                      to provide Bookings or Backlog data to support our
                      analysis of the Sales data).
                    </li>
                    <li>
                      OR you were asked to upload the data for analysis by ITR
                      Economics (for example, you may be asked to upload new
                      datasets if you are considering changing the data included
                      in your reports).
                    </li>
                  </ul>
                </template>
              </BaseTooltip>
            </span>
          </label>
          <select
            id="upload-is-consulting"
            v-model="form.data.isPortalData"
            :class="{
              'datasets-upload-page--is-invalid': !!form.error.isPortalData,
            }"
          >
            <option selected :value="null">Select One</option>
            <option :value="0">Yes</option>
            <option :value="1">No</option>
          </select>
        </div>
      </section>
      <hr />
      <section class="datasets-upload-page__form-section">
        <div class="datasets-upload-page__form-container">
          <label for="data-name" class="datasets-upload-page__form-label">
            <span>Data Name</span>
            <span class="datasets-upload-page__icon-container">
              <IconAlertCircle v-if="form.error.name" />
            </span>
          </label>
          <input
            id="data-name"
            v-model="form.data.name"
            type="text"
            :class="{
              'datasets-upload-page--is-invalid': !!form.error.name,
            }"
            @input="validateFormNameInput()"
            @blur="validateFormName()"
          />
        </div>
        <div class="datasets-upload-page__form-container">
          <label
            for="data-description"
            class="datasets-upload-page__form-label"
          >
            <span>Data Description</span>
            <span class="datasets-upload-page__icon-container">
              <IconAlertCircle v-if="form.error.description" />
            </span>
          </label>
          <input
            id="data-description"
            v-model="form.data.description"
            type="text"
            :class="{
              'datasets-upload-page--is-invalid': !!form.error.description,
            }"
            @input="validateFormDescriptionInput()"
            @blur="validateFormDescription()"
          />
        </div>
      </section>
      <section class="datasets-upload-page__form-section">
        <div class="datasets-upload-page__form-container">
          <label for="data-type" class="datasets-upload-page__form-label">
            <span>Data Type</span>
            <span class="datasets-upload-page__icon-container">
              <IconAlertCircle v-if="form.error.type" />
            </span>
          </label>
          <select
            v-if="dropdownData"
            id="data-type"
            v-model="form.data.type"
            :class="{
              'datasets-upload-page--is-invalid': !!form.error.type,
            }"
            @change="validateFormTypeInput()"
            @blur="validateFormType()"
          >
            <option
              v-for="dataType in dropdownData.data_types"
              :key="`data-type-${dataType['Text']}`"
              :value="dataType['Text']"
            >
              {{ dataType['Text'] }}
            </option>
          </select>
        </div>
        <div class="datasets-upload-page__form-container">
          <label for="data-base" class="datasets-upload-page__form-label">
            <span>Base</span>
            <span class="datasets-upload-page__icon-container">
              <IconAlertCircle v-if="form.error.base" />
            </span>
          </label>
          <select
            v-if="dropdownData"
            id="data-base"
            v-model="form.data.base"
            :class="{
              'datasets-upload-page--is-invalid': !!form.error.base,
            }"
            @change="validateFormBaseInput()"
            @blur="validateFormBase()"
          >
            <option
              v-for="dataBase in dropdownData.data_base"
              :key="`data-base-${dataBase['Text']}`"
              :value="dataBase['Text']"
            >
              {{ dataBase['Text'] }}
            </option>
          </select>
        </div>
        <div class="datasets-upload-page__form-container">
          <label for="data-units" class="datasets-upload-page__form-label">
            <span>Units</span>
            <span class="datasets-upload-page__icon-container">
              <IconAlertCircle v-if="form.error.units" />
            </span>
          </label>
          <select
            v-if="dropdownData"
            id="data-units"
            v-model="form.data.units"
            :class="{
              'datasets-upload-page--is-invalid': !!form.error.units,
            }"
            @change="validateFormUnitsInput()"
            @blur="validateFormUnits()"
          >
            <option
              v-for="dataUnit in dropdownData.data_units"
              :key="`data-unit-${dataUnit['Text']}`"
              :value="dataUnit['Text']"
            >
              {{ dataUnit['Text'] }}
            </option>
          </select>
        </div>
      </section>
      <hr />
      <section class="datasets-upload-page__submission-container">
        <DatasetDropfile
          :selected-file="form.data.file"
          :has-error="form.error.file"
          @file-uploaded="handleFileUpload"
          @clear-file="form.data.file = null"
          :isPortalData="form.data.isPortalData"
        />
        <div
          class="datasets-upload-page__form-container datasets-upload-page__notes-container"
        >
          <label for="data-notes" class="datasets-upload-page__form-label">
            <span>Notes</span>
            <span class="datasets-upload-page__icon-container">
              <IconAlertCircle v-if="this.form.error.notes" />
            </span>
          </label>
          <textarea
            id="data-notes"
            v-model="form.data.notes"
            placeholder="Are there any significant changes to your business that would affect the data moving forward (new products, new efficiencies, pricing problems, etc.)? The more of these events we know about the more we are able to incorporate into our analysis.
If you answer “yes” to any of the below, please provide detailed notes to explain.
        - Are there “0s” in the data?
        - Was the data history revised in any way?
        - Are there missing months of data?
        - Is there a month or months of data that have deviated from forecast?"
            class="datasets-upload-page__form-notes"
            :class="{
              'datasets-upload-page__form-notes--error': this.form.error.notes,
            }"
            @input="validateFormNotesInput()"
          />
          <BaseButton
            type="submit"
            class="datasets-upload-page__form-submit-button"
          >
            <span v-if="form.data.isPortalData">UPLOAD DATASET</span>
            <span v-else>
              SUBMIT DATA
              <br />
              REQUEST
            </span>
          </BaseButton>
        </div>
      </section>
    </form>
    <ProfileCompanyConfirmPrompt ref="confirm-prompt" />
    <ProfileCompanyModal ref="company-profile" />
    <ItrLoading v-if="isSubmitting" infinite />
    <DatasetUploadWarningDialog ref="dataset-upload-warning-dialog" />
    <BaseDialog ref="confirmation-dialog" :padding="false" />
  </section>
</template>

<script>
  import _ from 'lodash';
  import axios from 'axios';
  import ProfileCompanyConfirmPrompt from '~/components/dashboard/my-data/profile/ProfileCompanyConfirmPrompt.vue';
  import ProfileCompanyModal from '~/components/dashboard/my-data/profile/ProfileCompanyModal.vue';
  import DatasetDropfile from '~/components/dataset/DatasetDropfile.vue';
  import IconAlertCircle from '~/components/icon/IconAlertCircle.vue';
  import ItrLoading from '~/components/ItrLoading.vue';
  import BaseTooltip from '~/components/base/BaseTooltip.vue';
  import DatasetUploadWarningDialog from '~/components/dataset/DatasetUploadWarningDialog.vue';
  import IconInformationCircle from '~/components/icon/IconInformation.vue';
  import BaseButton from '~/components/base/BaseButton.vue';
  import BaseDialog from '~/components/base/BaseDialog.vue';

  export default {
    name: 'MyDatasetsUpload',
    components: {
      ProfileCompanyConfirmPrompt,
      ProfileCompanyModal,
      DatasetDropfile,
      IconAlertCircle,
      ItrLoading,
      BaseTooltip,
      DatasetUploadWarningDialog,
      IconInformationCircle,
      BaseButton,
      BaseDialog,
    },
    props: {
      user: {
        type: Object,
        required: true,
      },
      dataset: {
        type: Object,
        required: false,
      },
    },
    data: () => ({
      isLoading: true,
      isSubmitting: false,
      dropdownData: null,
      needsToCompleteCompanyProfile: false,
      hasConfirmedWarning: false,
      subscriptions: [],
      form: {
        data: {
          isPortalData: null,
          name: '',
          description: '',
          type: '',
          base: '',
          units: '',
          file: null,
          notes: '',
        },
        error: {
          isPortalData: false,
          name: false,
          description: false,
          type: false,
          base: false,
          units: false,
          file: false,
          notes: false,
        },
      },
    }),
    computed: {
      formHasError() {
        return (
          this.form.error.isPortalData ||
          this.form.error.name ||
          this.form.error.description ||
          this.form.error.type ||
          this.form.error.base ||
          this.form.error.units ||
          this.form.error.file ||
          this.form.error.notes
        );
      },
      hasAllianceSubscription() {
        return this.subscriptions.some((item) => item.includes('Alliance'));
      },
      hasConsultingSubscription() {
        return this.user.consulting_access;
      },
      hasDatacastSubscription() {
        return this.subscriptions.some(
          (item) => item.includes('Datacast') || item.includes('DataCast')
        );
      },
      showPortalData() {
        return (
          this.hasConsultingSubscription &&
          (this.hasAllianceSubscription || this.hasDatacastSubscription)
        );
      },
    },
    async created() {
      this.subscriptions = this.user.subscriptions
        .concat(this.user.active_free_trials)
        .map((subscription) => {
          return subscription.active ? subscription.product.name : '';
        });

      this.setPortalData();
      this.isLoading = false;
      try {
        const { data } = await axios.get('/dashboard/dataset/dropdowns');
        this.dropdownData = data;
        this.form.data.type = data.data_types.find(
          (type) => type.Selected === true
        ).Text;
        this.form.data.base = data.data_base.find(
          (base) => base.Selected === true
        ).Text;
        this.form.data.units = data.data_units.find(
          (unit) => unit.Selected === true
        ).Text;
      } catch (err) {
        // TODO: HANDLE ERROR - This is just silencing the error
      }
      try {
        const { data } = await axios.get(
          '/dashboard/datasets/company-profile/status'
        );
        this.needsToCompleteCompanyProfile = data.IsValid;
      } catch (err) {
        // TODO: HANDLE ERROR - This is just silencing the error
      }
      if (this.dataset) {
        this.form.data.isPortalData = this.dataset.IsPortalData ? 1 : 0;
        this.form.data.name = this.dataset.Name;
        this.form.data.description = this.dataset.Description;
        this.form.data.type = this.dataset.DataTypes.find(
          (type) => type.Selected === true
        ).Text;
        this.form.data.base = this.dataset.Base.find(
          (base) => base.Selected === true
        ).Text;
        this.form.data.units = this.dataset.Units.find(
          (unit) => unit.Selected === true
        ).Text;
      }
    },
    methods: {
      handleFileUpload(file) {
        this.hasConfirmedWarning = false;
        this.form.data.file = file;
        this.validateFormFile();
      },
      fileExists(files, fileId) {
        return files.some(({ id }) => id === fileId);
      },
      async submitForm() {
        this.validateFormIsPortalData();
        this.validateFormName();
        this.validateFormDescription();
        this.validateFormType();
        this.validateFormBase();
        this.validateFormUnits();
        this.validateFormFile();
        this.validateFormNotes();

        if (this.formHasError) return;
        let fileName = this.form.data.file.name;
        let fileNameArray = fileName.split('.');
        let fileExtension = fileNameArray[fileNameArray.length - 1];
        if (!fileExtension.includes('xls') || !fileExtension.includes('xlsx')) {
          await this.$refs['confirmation-dialog'].prompt({
            title:
              '<span style="font-size: 20px; font-weight: 700">File type is not valid.</span>',
            message: `<span style="color: var(--itr-blue)">Please select an Excel file for upload.</span>`,
            confirmText: 'Okay',
            level: 'success',
          });
        }

        try {
          this.isSubmitting = true;

          const config = {
            headers: { 'content-type': 'multipart/form-data' },
          };

          let formData = new FormData();

          formData.append('isPortalData', this.form.data.isPortalData);
          formData.append('name', this.form.data.name);
          formData.append('description', this.form.data.description);
          formData.append('type', this.form.data.type);
          formData.append('base', this.form.data.base);
          formData.append('units', this.form.data.units);
          formData.append('file', this.form.data.file);
          formData.append('notes', this.form.data.notes);
          formData.append('hasConfirmedWarnings', this.hasConfirmedWarning);
          if (this.dataset) {
            formData.append('id', this.dataset.ID);
            formData.append('doscode', this.dataset.DOSCode);
          }
          await axios.post('/dashboard/dataset/upload', formData, config);

          if (
            this.needsToCompleteCompanyProfile &&
            this.form.data.isPortalData === 0
          ) {
            this.isSubmitting = false;
            let hasConfirmed = false;

            try {
              hasConfirmed = await this.$refs['confirm-prompt'].prompt();
            } catch (err) {
              this.isSubmitting = true;
            }

            if (hasConfirmed) {
              try {
                const response = await this.$refs['company-profile'].prompt();
                this.isSubmitting = true;
                if (response) {
                  // IF THE USER SELECTED SAVE PROFILE, WE CAN HANDLE THAT SPECIFICALLY HERE
                } else {
                  // IF THE USER SELECTED COMPLETE LATER, WE CAN HANDLE THAT SPECIFICALLY HERE
                }
              } catch (err) {
                // IF THERE WAS AN ERROR UPLOADING THE COMPANY PROFILE, WE NEED TO HANDLE THE ERROR
                // TODO: HANDLE ERROR - This is just silencing the error
              }
            }
          }
          if (this.dataset) {
            window.location.replace(
              `/dashboard/datasets?upload=success&replace=true&dataset_type=${this.form.data.isPortalData ? 'datacast' : 'consulting'}`
            );
          }
          window.location.replace(
            `/dashboard/datasets?upload=success&dataset_type=${this.form.data.isPortalData ? 'datacast' : 'consulting'}`
          );
        } catch (err) {
          this.isSubmitting = false;
          const errorMessage = err.response.data.message;

          if (errorMessage && errorMessage === 'Attention Needed.') {
            try {
              const futureDataPoints = err.response.data.futureDataPoints;
              const zeroDataPoints = err.response.data.zeroDataPoints;
              if (this.form.data.isPortalData) {
                if (zeroDataPoints.length) {
                  await this.$refs['dataset-upload-warning-dialog'].prompt({
                    zeroDataPoints,
                  });
                } else {
                  this.hasConfirmedWarning = true;
                }
              } else {
                await this.$refs['dataset-upload-warning-dialog'].prompt({
                  futureDataPoints,
                  zeroDataPoints,
                });
              }
              this.hasConfirmedWarning = true;
            } catch (err) {
              // TODO: HANDLE ERROR - This is just silencing the error
            }
          }

          if (errorMessage && errorMessage === 'No data found in excel file') {
            this.form.error.file = true;
          }

          if (errorMessage && errorMessage === 'Invalid Excel File') {
            this.form.error.file = true;
            await this.$refs['confirmation-dialog'].prompt({
              title:
                '<span style="font-size: 20px; font-weight: 700">Invalid Excel file.</span>',
              message: `<span style="color: var(--itr-blue)">Please download the latest Dataset Template.</span>`,
              confirmText: 'Okay',
              level: 'success',
            });
          }
        }
      },
      validateFormIsPortalData() {
        this.form.error.isPortalData = false;
        if (
          this.form.data.isPortalData === null ||
          this.form.data.isPortalData === undefined
        ) {
          this.form.error.isPortalData = true;
        }
      },
      validateFormName() {
        this.form.error.name = false;
        if (this.form.data.name.length <= 0) {
          this.form.error.name = true;
        }
      },
      validateFormNameInput: _.debounce(function validateFormNameInput() {
        this.validateFormName();
      }, 250),
      validateFormDescription() {
        this.form.error.description = false;
        if (this.form.data.isPortalData === 1) return;
        if (this.form.data.description.length <= 0) {
          this.form.error.description = true;
        }
      },
      validateFormDescriptionInput: _.debounce(
        function validateFormDescriptionInput() {
          this.validateFormDescription();
        },
        250
      ),
      validateFormType() {
        this.form.error.type = false;
        if (this.form.data.isPortalData === 1) return;
        if (!this.form.data.type || this.form.data.type === 'Select One') {
          this.form.error.type = true;
        }
      },
      validateFormTypeInput: _.debounce(function validateFormTypeInput() {
        this.validateFormType();
      }, 250),
      validateFormBase() {
        this.form.error.base = false;
        if (this.form.data.isPortalData === 1) return;
        if (!this.form.data.base || this.form.data.base === 'Select One') {
          this.form.error.base = true;
        }
      },
      validateFormBaseInput: _.debounce(function validateFormBaseInput() {
        this.validateFormBase();
      }, 250),
      validateFormUnits() {
        this.form.error.units = false;
        if (this.form.data.isPortalData === 1) return;
        if (!this.form.data.units || this.form.data.units === 'Select One') {
          this.form.error.units = true;
        }
      },
      validateFormUnitsInput: _.debounce(function validateFormUnitsInput() {
        this.validateFormUnits();
      }, 250),
      validateFormFile() {
        this.form.error.file = false;
        if (!this.form.data.file) {
          this.form.error.file = true;
        }
      },
      validateFormNotes() {
        this.form.error.notes = false;
        if (this.form.data.isPortalData === 1) return;
        if (this.form.data.notes.length <= 0) {
          this.form.error.notes = true;
        }
      },
      validateFormNotesInput: _.debounce(function validateFormNotesInput() {
        this.validateFormNotes();
      }, 250),
      setPortalData() {
        if (
          this.hasConsultingSubscription &&
          (this.hasAllianceSubscription || this.hasDatacastSubscription)
        ) {
          return;
        }
        if (this.hasConsultingSubscription) {
          this.form.data.isPortalData = 0;
          return;
        }
        if (this.hasAllianceSubscription || this.hasDatacastSubscription) {
          this.form.data.isPortalData = 1;
          return;
        }
      },
    },
    watch: {
      'form.data.isPortalData': function (newValue, oldValue) {
        if (newValue !== null && this.form.error.isPortalData) {
          this.form.error.isPortalData = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .no-margin {
    margin: 0;
  }
  .bold {
    font-weight: 700;
  }
  .datasets-upload-page {
    padding: 15px 45px;

    &--is-invalid {
      border-color: #eb5757 !important;
    }

    &__header {
      display: flex;
      flex-direction: column;
      width: 100%;
      z-index: 0;
    }
    &__header-title {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
      padding: 15px;

      h1 {
        margin: 0;
      }
      p {
        flex: 1 auto;
        padding: 0 40px;
      }
    }
    &__display-field {
      color: #a7a9ab !important;
      border: none !important;
      padding: 0 !important;
      min-height: fit-content !important;
      height: 100%;
    }
    &__form {
      display: flex;
      flex-direction: column;
      width: 100%;
    }
    &__form-label {
      display: flex;
      align-items: center;
      gap: 5px;
      font-size: 16px;
      flex: 1;
    }
    &__form-label.extra-gap {
      gap: 15px;
    }
    &__form-section {
      display: flex;
      flex-direction: column;
      width: 100%;
      gap: 15px;
    }
    &__form-container {
      display: flex;
      flex-direction: column;
      width: 100%;
      padding: 15px;

      &--meta-data {
        width: 33.333%;
      }
    }
    &__form-notes {
      min-height: 135px;
      &::placeholder {
        font-style: italic;
        color: var(--itr-grey-mid);
      }
      &--error {
        border-color: #eb5757 !important;
      }
    }
    &__submission-container {
      display: flex;
      flex-direction: column;
      width: 100%;
      gap: 15px;
    }
    &__notes-container {
      // align-items: center;
    }
    &__form-submit-button {
      width: fit-content;
      margin: 10px 0;
      line-height: 1.4;
    }
    &--error {
      color: #eb5757;
      font-style: italic;
      font-size: 14px;
      font-weight: 700;
    }

    hr {
      border: 1px solid #3075a4;
      margin: 25px 0 45px 0;
    }

    &__icon-container {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
    }
  }

  @include screen-lg {
    .datasets-upload-page {
      &__header-title,
      &__form-section {
        flex-direction: row;
      }
    }
  }
  @include screen-xxl {
    .datasets-upload-page {
      &__submission-container {
        flex-direction: row;
        > * {
          width: 50%;
        }
      }
    }
  }
</style>
